<template>
  <base-drawer
    class="edit-user-drawer drawer-form"
    :show="show"
    :title="title"
    width="480px"
    @close="onClose"
  >
    <drawer-section title="Account" icon="icon-single-02">
      <info-hint>
        Changing the email replaces the sign-in address. The person gets a new invite at the new address and keeps their history.
      </info-hint>
      <base-input
        class="mt-3"
        label="Email"
        type="email"
        placeholder="name@yourvenue.com"
        v-model="form.email"
        :error="fieldErrors.email"
        autocomplete="off"
      />
    </drawer-section>

    <drawer-section title="Access" icon="icon-lock-circle">
      <base-input label="Role" :error="fieldErrors.role">
        <el-select
          v-model="form.roleKey"
          class="select-primary"
          placeholder="Choose a role"
        >
          <el-option
            v-for="role in roles"
            :key="roleKey(role)"
            class="select-primary"
            :label="role.name"
            :value="roleKey(role)"
          >
            <span>{{ role.name }}</span>
            <span v-if="role.default_role" class="text-muted ml-2">default</span>
          </el-option>
        </el-select>
      </base-input>

      <base-input label="Groups" :error="fieldErrors.groups">
        <el-select
          multiple
          v-model="form.groupIds"
          class="select-primary"
          placeholder="Choose one or more groups"
        >
          <el-option
            v-for="group in groups"
            :key="group.id"
            class="select-primary"
            :label="group.name"
            :value="group.id"
          />
        </el-select>
      </base-input>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="save">
          <i v-if="saving" class="el-icon-loading mr-1"></i>Save changes
        </base-button>
      </div>
    </template>

    <base-alert-dialog
      :show="alert.show"
      variant="danger"
      :title="alert.title"
      :message="alert.message"
      confirm-text="Ok"
      @confirm="alert.show = false"
      @cancel="alert.show = false"
    />
  </base-drawer>
</template>

<script setup lang="ts">
// Edit a staff user's email, role and groups. PUT /companies/{id}/users
// (companies-edit-company-user) expects { id, email, role_id, default_role, groups }.
import { ref, reactive, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { isValidEmail } from '@/util/emailValidator'

type Role = { id: number; name: string; default_role: number }
type Group = { id: number; name: string }

const props = defineProps({
  show: { type: Boolean, default: false },
  user: { type: Object, default: null },
  roles: { type: Array as () => Role[], default: () => [] },
  groups: { type: Array as () => Group[], default: () => [] },
})

const emit = defineEmits(['update:show', 'close', 'saved'])

const { proxy } = getCurrentInstance() as any
const companyId = localStorage.getItem('companyId')

const saving = ref(false)
const form = ref({ email: '', roleKey: '', groupIds: [] as number[] })
const fieldErrors = reactive({ email: '', role: '', groups: '' })
const alert = reactive({ show: false, title: '', message: '' })

// Default roles and custom roles can share numeric ids, so the select keys on both.
function roleKey(role: Role) {
  return `${role.default_role ? 'd' : 'c'}:${role.id}`
}

const title = computed(() => {
  const u = props.user
  if (!u) return 'Edit user'
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ')
  return name ? `Edit ${name}` : 'Edit user'
})

function loadForm() {
  const u = props.user
  if (!u) return
  let selected = ''
  if (u.default_role_id) selected = `d:${u.default_role_id}`
  else if (u.role_id) selected = `c:${u.role_id}`
  let groupIds: number[] = []
  try {
    const parsed = typeof u.user_groups === 'string' ? JSON.parse(u.user_groups) : (u.user_groups || [])
    groupIds = parsed.map((g: any) => g.id).filter((id: number) => props.groups.some(g => g.id === id))
  } catch (e) {
    groupIds = []
  }
  form.value = { email: u.email || '', roleKey: selected, groupIds }
  fieldErrors.email = ''
  fieldErrors.role = ''
  fieldErrors.groups = ''
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function validate(): boolean {
  fieldErrors.email = ''
  fieldErrors.role = ''
  fieldErrors.groups = ''
  const email = form.value.email.trim()
  if (!email) fieldErrors.email = 'Enter an email'
  else if (!isValidEmail(email)) fieldErrors.email = 'Enter a valid email'
  if (!form.value.roleKey) fieldErrors.role = 'Choose a role'
  if (form.value.groupIds.length === 0) fieldErrors.groups = 'Choose at least one group'
  return !fieldErrors.email && !fieldErrors.role && !fieldErrors.groups
}

async function save() {
  if (!props.user || !validate()) return
  const [kind, idText] = form.value.roleKey.split(':')
  saving.value = true
  try {
    const config = await authConfig()
    await axios.put(`${API_LOCATION}companies/${companyId}/users`, {
      id: props.user.id,
      email: form.value.email.trim(),
      role_id: Number(idText),
      default_role: kind === 'd' ? 1 : 0,
      groups: form.value.groupIds,
    }, config)
    proxy.$notify({ type: 'success', message: 'User updated', icon: 'tim-icons icon-check-2' })
    emit('saved')
    onClose()
  } catch (err: any) {
    const status = err?.response?.status
    const data = err?.response?.data
    if (status === 401) {
      proxy.$router.push('/forbidden')
    } else {
      alert.title = 'Changes not saved'
      alert.message = typeof data === 'string' && data ? data : 'Something went wrong. Please try again.'
      alert.show = true
    }
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && !alert.show) onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
}, { immediate: true })

onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))
</script>

<script lang="ts">
export default { name: 'EditUserDrawer' }
</script>
